import React from "react";
import { Box, Typography } from "@mui/material";
import LinkifiedText from "./LinkifiedText.jsx";
import ReadReceipt from "./ReadReceipt.jsx";

const ComprehensiveMessage = ({
  onJumpToOriginal,
  msg,
  isSentByUser,
  senderName,
  hoveredMessageId,
  setHoveredMessageId,
  HoverActions,
  setReplyToMessage,
  handleOpenThread,
  onForward,
  isLastRead,
  currentUser,
}) => {
  const sentDate = new Date(msg.sentAt);

  const isSystemMessage = msg.content?.includes("[") && msg.content?.includes("]");
  let cleanContent = msg.content || "";
  let systemStatus = null;

  if (isSystemMessage) {
    if (cleanContent.toLowerCase().includes("approval")) {
      systemStatus = "approval";
      cleanContent = cleanContent.replace(/\[approval\]/gi, "").trim();
    } else if (cleanContent.toLowerCase().includes("rework")) {
      systemStatus = "rework";
      cleanContent = cleanContent.replace(/\[rework\]/gi, "").trim();
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isSentByUser ? 'flex-end' : 'flex-start',
        width: '100%',
        transition: 'all 0.2s ease',
        marginBottom: '4px'
      }}
    >
      <Box
        className="message-bubble"
        onMouseEnter={() => setHoveredMessageId(msg._id)}
        onMouseLeave={() => setHoveredMessageId(null)}
        sx={{
          maxWidth: '45%',
          borderRadius: '12px',
          overflow: 'hidden',
          background: isSentByUser ? '#E0F2FE' : '#FFFFFF',
          color: '#1E293B',
          position: 'relative',
          border: '1px solid #E2E8F0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          transition: 'all 0.2s ease',
        }}
      >
        {/* Internal Header */}
        <Box sx={{ 
          px: '12px', 
          py: '4px', 
          background: isSentByUser ? 'rgba(0,0,0,0.04)' : 'rgba(0,0,0,0.02)',
          borderBottom: '1px solid rgba(0,0,0,0.05)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 4
        }}>
          <Typography sx={{ fontSize: '11px', fontWeight: 700, opacity: 0.9 }}>
            {isSentByUser ? 'Me' : senderName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{ fontSize: '9px', opacity: 0.7 }}>
              {sentDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ px: '12px', py: '10px' }}>
          {msg.isForwarded && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, opacity: 0.6 }}>
               <Typography sx={{ fontSize: '10px', fontWeight: 700, fontStyle: 'italic' }}>
                 ➡️ Forwarded {msg.originalSender ? `from ${msg.originalSender}` : ""}
               </Typography>
            </Box>
          )}

          {hoveredMessageId === msg._id && (
            <Box sx={{ position: 'absolute', top: 2, right: 2, zIndex: 10, scale: '0.8' }}>
              <HoverActions
                message={{ ...msg, senderName }}
                onReply={(message) => setReplyToMessage(message)}
                onOpenThread={handleOpenThread}
                onForward={onForward}
              />
            </Box>
          )}

          {msg.isReply && (
            <Box
              onClick={(e) => { e.stopPropagation(); onJumpToOriginal(msg.replyTo); }}
              sx={{
                background: isSentByUser ? 'rgba(0,0,0,0.03)' : '#F8FAFD',
                borderRadius: '8px',
                padding: '8px 10px',
                marginBottom: '8px',
                borderLeft: `3px solid #60A5FA`,
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                cursor: 'pointer',
                '&:hover': { background: isSentByUser ? 'rgba(0,0,0,0.05)' : '#F1F5F9' }
              }}
            >
              <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#60A5FA', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {msg.replyToSender || "User"}
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  color: '#475569',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: "'Inter', sans-serif"
                }}
              >
                {msg.replyToContent}
              </Typography>
            </Box>
          )}

          {systemStatus && (
            <Box
              sx={{
                display: 'inline-block',
                px: '10px',
                py: '3px',
                borderRadius: '999px',
                fontSize: '10px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginBottom: '8px',
                background: systemStatus === "approval" ? 'rgba(16, 185, 129, 0.1)' : 'rgba(96, 165, 250, 0.12)',
                color: systemStatus === "approval" ? '#10B981' : '#60A5FA',
                border: `1px solid ${systemStatus === "approval" ? 'rgba(16, 185, 129, 0.2)' : 'rgba(96,165,250,0.22)'}`
              }}
            >
              {systemStatus}
            </Box>
          )}

          {(msg.taskId || msg.dueDate) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px', p: '10px', background: 'rgba(0,0,0,0.02)', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.05)' }}>
              {msg.taskId && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Task ID</Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#334155' }}>{msg.taskId}</Typography>
                </Box>
              )}
              {msg.dueDate && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Due Date</Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#334155' }}>{new Date(msg.dueDate).toLocaleDateString("en-GB")}</Typography>
                </Box>
              )}
            </Box>
          )}

          <Box
            sx={{
              fontSize: '13px',
              lineHeight: 1.5,
              fontFamily: "'Inter', sans-serif",
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            <LinkifiedText text={cleanContent} />
          </Box>

          {msg.thread && (
            <Box
              onClick={(e) => { e.stopPropagation(); handleOpenThread(msg._id); }}
              sx={{
                mt: '8px',
                fontSize: '11px',
                fontWeight: 600,
                color: '#60A5FA',
                cursor: 'pointer',
                textAlign: 'right',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              {msg.threadCount || 0} replies
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ComprehensiveMessage;
